import * as Sentry from '@sentry/react'

export function initializeSentry() {
  // Only initialize Sentry when explicitly enabled (remote dev/staging/production)
  // Will NOT run on local development unless VITE_SENTRY_ENABLED is set to 'true'
  if (
    import.meta.env.VITE_SENTRY_ENABLED === 'true' &&
    import.meta.env.VITE_SENTRY_DSN &&
    import.meta.env.VITE_SENTRY_ENVIRONMENT
  ) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, // Capture 100% of the transactions
      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      // SDK v11 collects user info, cookies and HTTP bodies by default; keep
      // the v10 (sendDefaultPii off) baseline — the site handles guests' PII.
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
          response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        },
        httpBodies: [],
        urlQueryParams: {
          deny: ['forwarded', '-ip', 'remote-', 'via', '-user'],
        },
      },

      // Filter out non-actionable errors
      beforeSend(event) {
        const errorType = event.exception?.values?.[0]?.type || 'Unknown'
        const errorMessage = event.exception?.values?.[0]?.value || ''

        // Check if error involves our own API (check error message, not request URL)
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
        const isOwnApiError = apiBaseUrl && errorMessage.includes(apiBaseUrl)

        // Network errors - only filter if NOT from our own API
        const isNetworkError =
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Network request failed') ||
          errorMessage.includes('Load failed')

        // Filter non-actionable errors
        const shouldFilter =
          // Android WebView bridge errors in Facebook/Instagram in-app browsers
          errorMessage.includes('Java object is gone') ||
          // iOS WebKit messageHandlers errors in non-native contexts
          errorMessage.includes('window.webkit.messageHandlers') ||
          // User-initiated share cancellations (specific messages)
          errorMessage.includes('Share canceled') ||
          errorMessage.includes('cancellation of share') ||
          // Aborted operations (user navigation, query cancellation) - specific checks only
          errorType === 'AbortError' ||
          errorMessage === 'The operation was aborted' ||
          errorMessage === 'signal is aborted without reason' ||
          // Temporary network issues (only for third-party requests, not our API)
          (isNetworkError && !isOwnApiError) ||
          // Microsoft Clarity analytics errors (specific check)
          errorMessage.includes('window.clarity') ||
          errorType === 'ClarityError'

        if (shouldFilter) {
          return null
        }

        // Surface reported errors in development for debugging
        if (import.meta.env.DEV) {
          console.warn('[Sentry]', errorType, errorMessage, event)
        }

        return event
      },
    })
  }
}
